<template>
    <InputField
        ref="input"
        v-model="phoneNumber"
        class="phone-input-field"
        type="tel"
        :name="name"
        :label="label || $designSystem.i18n.messages['phone.input.label']"
        :invalid="isInvalid"
        :maxlength="30"
        :submitted="submitted"
        :required="required"
        :readonly="readonly"
        :autocomplete="autocomplete"
        @blur="updatePhoneNumber"
    >
        <template #error>
            <slot name="error" />
        </template>

        <template #help>
            <slot name="help" />
        </template>
    </InputField>
</template>

<script>
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';

import { InputField } from '../InputField';

export default {
    name: 'DsPhoneInput',

    components: {
        InputField,
    },

    props: {
        /**
         * Label to render above the phone field when active
         */
        label: String,

        /**
         * HTML input element name
         */
        name: {
            type: String,
            default: 'phone',
        },

        /**
         * Country to format the phone input with
         */
        country: {
            type: String,
            default: 'US',
        },

        /**
         * Controls visualization of rendering form validation error states
         */
        submitted: Boolean,

        /**
         * Required field for validation purposes
         */
        required: Boolean,

        /**
         * The value value of the phone number
         */
        value: [String, Number],

        /**
         * Forces invalid form field
         */
        invalid: Boolean,

        /**
         * Prevent user from typing in the input
         */
        readonly: Boolean,

        /**
         * Control whether you allow users to have the ability to autofill/autocomplete this input from things like Google
         */
        autocomplete: {
            type: [Boolean, String],
            default: false,
        },
    },

    data() {
        return {
            phoneNumber: this.value,
            valid: true,
        };
    },

    mounted() {
        this.load();
    },

    watch: {
        phoneNumber(value) {
            if (value !== this.value) {
                this.$emit('input', this.phoneNumber ? value : '');
            }
            this.checkIsValid();
        },

        value(value) {
            if (!value) {
                this.phoneNumber = '';
            }

            if (value !== this.phoneNumber) {
                this.phoneNumber = value;
            }
        },
    },

    computed: {
        isInvalid() {
            return !this.valid || this.invalid;
        },
    },

    methods: {
        updatePhoneNumber() {
            this.phoneNumber = new AsYouType(this.country).input(this.phoneNumber);
            this.$emit('blur');
            this.checkIsValid();
        },

        load() {
            let number = this.value;

            if (this.value) {
                const phoneNumber = parsePhoneNumber(this.value, this.country);

                if (this.country === phoneNumber?.country) {
                    number = phoneNumber.formatNational();
                }
            }

            if (number) {
                this.phoneNumber = new AsYouType(this.country).input(number);
            }

            this.checkIsValid();
        },

        checkIsValid() {
            if (!this.phoneNumber) {
                this.valid = !this.required;

                return;
            }

            this.valid = true;
        },
    },
};
</script>
